import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { DatePickerCalendar } from "react-nice-dates";
import {
  add,
  differenceInHours,
  endOfMonth,
  getHours,
  getMinutes,
  hoursToMilliseconds,
  minutesToMilliseconds,
  startOfDay,
  startOfHour,
  startOfMonth,
} from "date-fns";

import ru from "date-fns/locale/ru";

import Button from "../../components/Button/Button.jsx";
import ButtonsGroup from "../../components/ButtonsGroup/ButtonsGroup.jsx";

import WrapperContainer from "../../components/WrapperContainer/WrapperContainer.jsx";
import Error from "../../features/Error.jsx";
import ShimmerMultiLine from "../../components/Shimmer/ShimmerMultiLine.jsx";

import handleNotify from "../../utils/helpers/handleNotify.js";
import useRedirect from "../../hooks/useRedirect.js";

import { TECHNOLOGY_TYPE } from "../../utils/consts/technologyType";

import {
  apiChangeProcessEngineer,
  apiCreateProcessEngineer,
  apiFetchStatusProcess,
} from "../../api/methods/process.js";

import getTimeUtc5 from "../../utils/helpers/getTimeUtc5";
import "./style.scss";

/**
 * Смещение относительно UTC для часового пояса UTC+5
 */
const TZ_OFFSET_UTC5 = 5;

/**
 * Минимальное доступное время для выбора и максимально предельное время (10:00/20:00)
 */
const MIN_LIMIT_HOUR = 10;
const MAX_LIMIT_HOUR = 20;

const ChoiceDateTimeDeparture = () => {
  const history = useNavigate();
  const redirect = useRedirect();

  const { mobileConnectionInfo } = useSelector(state => state);
  const { apiKey } = mobileConnectionInfo;

  /**
   * Текущий отображаемый в календаре месяц.
   * Данный state позволяет обеспечить отображение месяца соответствующего дате,
   * которая находится в рамках данного месяца
   */
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const [selectedTime, setSelectedTime] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);

  // Тип технологии подключения на адресе
  const [typeConnection, setTypeConnection] = useState(TECHNOLOGY_TYPE.DRS.typeId);
  const [isFetchData, setIsFetchData] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await getAvailableDates(+new Date());
        setIsFetchData(true);
      } catch {
        setHasError(true);
      }
    })();
  }, []);

  /**
   * Получение перечня доступных для выезда дат.
   *
   * Для одних адресов по причине специфики подключения
   * будут доступны все дни просматриваемого месяца, для других лишь некоторые.
   * Там, где для выезда доступен любой день, сервер вернет days_list: null (иначе вернется массив дат) и
   * на начало каждого дня будет доступно для выбора любое время (с 09:00 до 19:00 включительно) с интервалом в один час
   *
   * @param {Number} time
   */
  const getAvailableDates = async time => {
    const { days_list } = await apiCreateProcessEngineer(apiKey, time);
    let dates = [];

    if (days_list) {
      dates = days_list.filter(date => date.timeSlotList?.length);
      setAvailableDates(dates);
    }
    setTypeConnection(days_list ? TECHNOLOGY_TYPE.PON.typeId : TECHNOLOGY_TYPE.DRS.typeId);
  };

  /**
   * Получить доступное время в формате HH:MM
   *
   * @param {Number} time
   * @return {String}
   */
  const getFormattedTime = time => {
    const timeToUtc5 = getTimeUtc5(time);
    const hours = getHours(timeToUtc5);

    return `${hours < 10 ? `0${hours}` : hours}:${
      getMinutes(timeToUtc5) === 0 ? "00" : getMinutes(timeToUtc5)
    }`;
  };

  /**
   * Первое доступное из всего перечня время.
   * Относительно данной переменной будет происходит
   * формирование конечно перечня доступного времени в UI
   *
   * @param {Date} currentDateTime
   * @param {Date} futureDateTime
   * @return {Number}
   */
  const getFirstAvailableHour = (currentDateTime, futureDateTime) => {
    const differenceHours = differenceInHours(futureDateTime, currentDateTime);

    if (differenceHours <= 0) {
      return getHours(add(currentDateTime, { hours: 2 }));
    } else {
      return futureDateTime.getHours();
    }
  };

  /**
   * Получить доступные часы для выезда
   *
   * @param {Date} date
   * @param {Number} hour
   * @return {Array}
   */
  const getAvailableTimes = (date, hour) => {
    let availableHour = hour;
    const currentDateTime = +new Date();
    const currentDate = startOfDay(currentDateTime);

    if (typeConnection === TECHNOLOGY_TYPE.PON.typeId) {
      const matchAvailableDate = availableDates.find(availableDate => {
        const time = getTimeUtc5(availableDate.date);
        return time === +startOfDay(date);
      });

      if (matchAvailableDate) {
        if (+currentDate === +date) {
          return matchAvailableDate.timeSlotList
            .filter(time => getHours(getTimeUtc5(time.timeFrom)) >= availableHour)
            .map(time => `${getFormattedTime(time.timeFrom)}`);
        } else {
          return matchAvailableDate.timeSlotList.map(time => `${getFormattedTime(time.timeFrom)}`);
        }
      } else {
        return [];
      }
    } else {
      if (+currentDate === +date) {
        if (availableHour < MIN_LIMIT_HOUR) {
          availableHour = MIN_LIMIT_HOUR;
        }
      } else {
        availableHour = MIN_LIMIT_HOUR;
      }

      const availableTimes = [];
      for (let i = availableHour; i < MAX_LIMIT_HOUR; i++) {
        availableTimes.push(`${i}:00`);
      }

      return availableTimes;
    }
  };

  /**
   * Выбрать доступное время для выезда.
   *
   * Время отображается целыми часами в виде HH:MM (например, 12:00).
   * Время доступное на начало выбранного дня должно корректироваться относительно текущего времени сервера.
   * То есть время доступное утром должно быть недоступным днем.
   * При этом сервер будет продолжать слать всё доступное время если оно не было занято
   *
   * @param {Date} selectedDate
   */
  const changeSelectedDate = async selectedDate => {
    setSelectedDate(selectedDate);
    setSelectedTime("");

    /**
     * Время относительно текущей даты со смещением на X часов
     * (в зависимости от типа подключения) 5 минут
     */
    let futureDateTime = add(new Date(), {
      hours: typeConnection ? TECHNOLOGY_TYPE.PON.hours : TECHNOLOGY_TYPE.DRS.hours,
      minutes: 5,
    });

    // Разница в часах между текущим временем сервера на момент выбора даты и будущим
    futureDateTime = startOfHour(futureDateTime);
    const firstAvailableHour = getFirstAvailableHour(new Date(), futureDateTime);

    const availableTimeList = getAvailableTimes(selectedDate, firstAvailableHour);
    setAvailableTimes(availableTimeList);
  };

  /**
   * Создать заявку на выезд
   */
  const createProcessEngineer = async () => {
    try {
      const hoursInitial = selectedTime.slice(0, 2);
      const minutesInitial = selectedTime.slice(3);

      let dateTimeDeparture = +selectedDate + hoursToMilliseconds(hoursInitial);
      if (+minutesInitial) {
        dateTimeDeparture += minutesToMilliseconds(minutesInitial);
      }

      const tzOffsetCurrent = new Date().getTimezoneOffset() / 60;
      const tzOffsetDelta = Math.abs(tzOffsetCurrent + TZ_OFFSET_UTC5);
      if (TZ_OFFSET_UTC5 >= Math.abs(tzOffsetCurrent)) {
        dateTimeDeparture = dateTimeDeparture - tzOffsetDelta * 3600000;
      } else {
        dateTimeDeparture = dateTimeDeparture + tzOffsetDelta * 3600000;
      }

      await apiChangeProcessEngineer(apiKey, dateTimeDeparture);

      const processStatus = await apiFetchStatusProcess(apiKey);
      redirect(processStatus);
    } catch (error) {
      handleNotify("error", error);
    }
  };

  /**
   * Перейти к другому месяцу в календаре.
   * При переходе запросить доступные дни месяца
   *
   * @param {Date} date
   */
  const changeMonth = async date => {
    try {
      setIsFetchData(false);
      setCurrentMonth(date);

      setSelectedDate(null);
      setSelectedTime("");

      await getAvailableDates(+date);
    } catch (error) {
      handleNotify("error", error);
    } finally {
      setIsFetchData(true);
    }
  };

  // Модификаторы определяют, какие классы CSS применяются к каждому календарному дню
  const modifiers = {
    // Недоступные для выбора дни соседних месяцев относительно просматриваемого
    disabled: date => {
      const dateSelectedMonth =
        +currentMonth === +startOfMonth(new Date()) ? new Date() : currentMonth;
      return startOfMonth(dateSelectedMonth) > date || endOfMonth(dateSelectedMonth) < date;
    },
    // Недоступные для выбора дни по причине отсутствия в эти дни свободного времени
    busy: date => {
      return (
        !availableDates.find(availableDate => {
          const time = getTimeUtc5(availableDate.date);
          return time === +date;
        }) && typeConnection === TECHNOLOGY_TYPE.PON.typeId
      );
    },
  };

  const getVisibilityBackButton = () => {
    let urlParams = new URLSearchParams(window.location.search);
    urlParams = Object.fromEntries(urlParams.entries());

    return urlParams.from;
  };

  if (hasError) return <Error />;
  if (!isFetchData) return <ShimmerMultiLine count={2} />;

  return (
    <>
      <WrapperContainer gap="16" title="sm">
        Выезд инженера
      </WrapperContainer>
      <WrapperContainer gap="24" text="lg">
        Выберите удобное время выезда для подключения Вас к сети.
      </WrapperContainer>
      <WrapperContainer gap="24">
        <DatePickerCalendar
          month={currentMonth}
          date={selectedDate}
          minimumDate={new Date()}
          maximumDate={add(new Date(), { months: 2 })}
          onMonthChange={date => changeMonth(date)}
          onDateChange={date => changeSelectedDate(date)}
          modifiers={modifiers}
          modifiersClassNames={{ busy: "-busy" }}
          weekdayFormat="eeeeee"
          locale={ru}
        />
      </WrapperContainer>

      {!!availableTimes.length && selectedDate && (
        <>
          <WrapperContainer gap="16" text="lg">
            Выберите время:
          </WrapperContainer>
          <WrapperContainer gap="24">
            <div className="list-available-time">
              {availableTimes.map((option, index) => (
                <div
                  key={index}
                  className={`list-available-time__time radio${
                    selectedTime === option ? " radio--active" : ""
                  }`}
                  style={
                    availableTimes.includes("00:00")
                      ? { flex: "initial", maxWidth: "initial" }
                      : null
                  }
                >
                  <input
                    type="radio"
                    className="radio__input"
                    name="availableTimes"
                    onChange={() => setSelectedTime(option)}
                    checked={option === selectedTime}
                    value={option}
                  />
                  {availableTimes.includes("00:00") ? "В течение дня" : option}
                </div>
              ))}
            </div>
          </WrapperContainer>
        </>
      )}

      {!availableTimes.length && selectedDate && (
        <WrapperContainer gap="24" text="lg">
          На выбранный день нет свободных инженеров
        </WrapperContainer>
      )}

      <ButtonsGroup>
        {getVisibilityBackButton() && (
          <Button isStretch color="secondary" size="xl" onClick={() => history(-1)}>
            Назад
          </Button>
        )}
        <Button
          isStretch
          color="primary"
          size="xl"
          onClick={createProcessEngineer}
          disabled={!selectedTime}
        >
          Далее
        </Button>
      </ButtonsGroup>
    </>
  );
};

export default ChoiceDateTimeDeparture;
